import videojs from 'video.js';

// TODO: Move back to using VideoJS `userActive` feature
// for controlling when the control bar is revealed to
// help with possible interactions with other plugins
const CLASS_REVEALED = 'vjs-wrp-control-bar-revealed';
// Selector for elements we don't want to count as interaction
const SELECTOR_IGNORE = `
  .vjs-wrp-button,
  .vjs-wrp-control,
  .vjs-control-bar,
  .vjs-big-play-button`;
// Threshold past which the user has moved enough that we consider it
// a pan when showing 360 videos
// https://github.com/videojs/videojs-vr/blob/eb80c8a55909be14a80ae93dfa295b7266f5aff4/src/canvas-player-controls.js#L83
const DISTANCE_FROM_ORIGIN_THRESHOLD = 10;

export class ControlInteractions extends videojs.getComponent('Component') {
  // Use our own option for inactivityTimeout so we can set the player option
  // to 0 to prevent the player from handling the inactivity as we're taking over
  constructor(player, { inactivityTimeout = 5000 } = {}) {
    super(player, {
      ...arguments[1],
      createEl: false,
    });

    const hideControlBar = () => {
      player.el().classList.remove(CLASS_REVEALED);
      clickCount = 0;
    };

    // Disables default events that listed for user activity.
    player.off([
      'mousemove',
      'mouseover',
      'mouseup',
      'mousedown',
      'mouseleave',
      'touchstart',
    ]);

    // Setting timeout on upper scope aids in cancelling it when needed.
    var reset = setTimeout(hideControlBar, inactivityTimeout);

    // Makes use of a click count to determine at which state this can be interacted with.
    var clickCount = 0;

    // TODO: Find a way to extract distance computation from this component
    // so it can be loaded conditionally only for the 360 videos. Ideally,
    // this would mean setting it up ahead of this component and either `preventDefault`
    // or `stopPropagation`
    let origin;
    let maxDistanceFromOrigin;

    this.on(player, ['touchstart', 'mousedown'], (event) => {
      origin = event;
      maxDistanceFromOrigin = 0;

      window.addEventListener('mousemove', track);
      window.addEventListener('mouseup', stopTracking);

      // Need to use capture phase as events' propagation get stopped
      // by either VideoJS or ThreeJS
      window.addEventListener('touchmove', track, true);
      window.addEventListener('touchend', stopTracking, true);
      window.addEventListener('touchcancel', stopTracking, true);
    });

    function track(event) {
      const dx = screenX(event) - screenX(origin);
      const dy = screenY(event) - screenY(event);
      maxDistanceFromOrigin = Math.max(
        maxDistanceFromOrigin,
        Math.sqrt(dx ** 2 + dy ** 2)
      );
    }

    function stopTracking() {
      window.removeEventListener('mousemove', track);
      window.removeEventListener('mouseup', stopTracking);

      window.removeEventListener('touchmove', track, true);
      window.removeEventListener('touchend', stopTracking, true);
      window.removeEventListener('touchcancel', stopTracking, true);
    }

    // We need to listen on both VideoJS `touchend` and `mouseup` events
    // as VideoJS prevents `touchend` on touch interactions and generates
    // its own `tap` events, which in turns prevents the `mouseup`
    // It may be worth just disabling VideoJS's `Component.prototype.emitTapEvents`
    // function as browsers nowadays handle `click` without delay when a touch
    // interaction happens
    this.on(player, ['touchend', 'mouseup'], (event) => {
      if (maxDistanceFromOrigin > DISTANCE_FROM_ORIGIN_THRESHOLD) {
        return;
      }
      // Used to prevent a choice from triggering this functionality.
      const shouldIgnore = event.target.closest(SELECTOR_IGNORE);

      if (!shouldIgnore) {
        // Clears scheduled resets, if any.
        clearTimeout(reset);

        // Increment count on each click.
        clickCount++;

        // Reveals control bar but nothing else may happen.
        if (clickCount === 1) {
          // Reveals bar.
          player.el().classList.add(CLASS_REVEALED);
          reset = setTimeout(hideControlBar, inactivityTimeout);
        }

        // Player can be interacted with.
        if (clickCount > 1) {
          if (player.paused() && !player.ended() && player.isVideo) {
            player.play();
            reset = setTimeout(hideControlBar, inactivityTimeout);
          } else if (clickCount > 1 && !player.paused()) {
            player.pause();
          }
        }
      }
    });
  }
}

function screenX(event) {
  return 'screenX' in event ? event.screenX : event.touches[0].screenX;
}

function screenY(event) {
  return 'screenY' in event ? event.screenY : event.touches[0].screenY;
}
